/**
 * ProfessionalCard — rose-accented card for the professionals index.
 *
 * Two variants:
 *   "grid" (default) — vertical card: circular photo Avatar, name, headline,
 *                      location line, language + expertise chips.
 *   "list"           — compact horizontal row with the same information.
 *
 * The whole card is clickable (the page wires `onClick` to
 * `/professionals/[slug]`). Data comes from `ProfessionalCardData`, the
 * card-level projection of `Professional.payload` (see types/professionals.ts).
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import { rose } from "@/theme/colors";
import { TAG_COLORS } from "@/data/presetTags";
import type { ProfessionalCardData } from "@/types/professionals";
import { cardActivationProps } from "@/utils/cardA11y";

export interface ProfessionalCardProps {
    professional: ProfessionalCardData;
    /** "grid" = vertical card (default); "list" = compact horizontal row. */
    variant?: "grid" | "list";
    /** Accent color for name, chips, and hover border. Defaults to rose. */
    accentColor?: string;
    onClick?: () => void;
    /**
     * Tags the viewer follows (profile followed disciplines). When provided,
     * expertise chips render filled iff followed — the organizations pages'
     * followed-tag treatment — instead of the uniform tinted look.
     */
    followedTags?: string[];
    /** Called with the expertise tag clicked (follow/unfollow confirm). */
    onTagClick?: (tag: string) => void;
}

const MAX_CHIPS = 3;

/**
 * Contact button — opens the visitor's own mail client with the
 * professional's address prefilled. Rendered only when a public email
 * exists, so profiles that withheld one show nothing rather than a dead
 * control.
 *
 * Label + icon where there's room; icon-only below `sm`, where the list
 * row needs the width for the name and tags. It stops propagation so it
 * doesn't also trigger the card's navigate-to-profile click.
 */
const ContactButton: React.FC<{
    email?: string;
    name: string;
    accentColor: string;
    compact?: boolean;
}> = ({ email, name, accentColor, compact = false }) => {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("md"));
    const trimmed = String(email ?? "").trim();
    if (!trimmed) return null;

    const label = `Email ${name}`;
    const open = (event: React.MouseEvent) => {
        // The whole card is a link; contacting is a different intent.
        event.stopPropagation();
        window.location.href = `mailto:${encodeURIComponent(trimmed)}`;
    };
    const iconOnly = compact || isSmall;

    if (iconOnly) {
        return (
            <Tooltip title={label}>
                <IconButton
                    size="small"
                    aria-label={label}
                    onClick={open}
                    sx={{
                        color: accentColor,
                        border: `1px solid ${alpha(accentColor, 0.5)}`,
                        "&:hover": {
                            backgroundColor: alpha(accentColor, 0.08),
                        },
                    }}
                >
                    <EmailIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        );
    }

    return (
        <Button
            size="small"
            variant="outlined"
            startIcon={<EmailIcon />}
            aria-label={label}
            onClick={open}
            sx={{
                color: accentColor,
                borderColor: alpha(accentColor, 0.5),
                textTransform: "none",
                flexShrink: 0,
                "&:hover": {
                    borderColor: accentColor,
                    backgroundColor: alpha(accentColor, 0.08),
                },
            }}
        >
            Contact
        </Button>
    );
};

/**
 * How many chips fit on one line of `width` px, estimated from label
 * length. Measuring each rendered chip would be exact but needs a second
 * layout pass per card; the estimate is stable and cheap, and the row
 * clips anyway so a near-miss degrades to a clean cut rather than a wrap.
 */
const chipsThatFit = (values: string[], width: number): number => {
    if (width <= 0) return values.length;
    const OVERFLOW_BADGE = 44; // room for the "+N" chip
    const PER_CHAR = 7.2;
    const PADDING = 26;
    const GAP = 6;
    let used = 0;
    let count = 0;
    for (const value of values) {
        const chipWidth = value.length * PER_CHAR + PADDING;
        const needed = used + chipWidth + (count > 0 ? GAP : 0);
        if (needed > width - OVERFLOW_BADGE) break;
        used = needed;
        count += 1;
    }
    // Always show at least one, otherwise a long single tag renders nothing.
    return Math.max(count, 1);
};

const formatLocation = (
    location?: ProfessionalCardData["location"],
): string => [location?.city, location?.country].filter(Boolean).join(", ");

/** Initials fallback for profiles without a photo. */
const initialsOf = (name: string): string =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("");

const ChipRow: React.FC<{
    values: string[];
    accentColor: string;
    filled?: boolean;
    /** Followed-tag mode: fill per-tag from this list, click to confirm. */
    followedTags?: string[];
    onTagClick?: (tag: string) => void;
    /** Darker shade for followed-chip hover; defaults to the accent. */
    accentDark?: string;
    /**
     * List view: measure the row and show only what fits on one line,
     * with a "+N" badge. The list row is very wide on desktop and very
     * narrow on tablet, so a fixed cap either wastes space or wraps.
     */
    fitToWidth?: boolean;
}> = ({
    values,
    accentColor,
    filled = false,
    followedTags,
    onTagClick,
    accentDark,
    fitToWidth = false,
}) => {
    const rowRef = useRef<HTMLDivElement | null>(null);
    const [available, setAvailable] = useState(0);

    // Re-measure on container resize so the count tracks the viewport.
    useEffect(() => {
        if (!fitToWidth) return;
        const node = rowRef.current;
        if (!node || typeof ResizeObserver === "undefined") return;
        const update = () => setAvailable(node.getBoundingClientRect().width);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(node);
        return () => ro.disconnect();
    }, [fitToWidth]);

    if (!values.length) return null;
    const limit = fitToWidth
        ? chipsThatFit(values, available)
        : MAX_CHIPS;
    const shown = values.slice(0, limit);
    const extra = values.length - shown.length;
    const followAware = followedTags !== undefined;
    return (
        <Box
            ref={rowRef}
            sx={{
                display: "flex",
                gap: 0.75,
                // Fitted rows stay on one line and clip; the grid card
                // keeps its original wrapping behaviour.
                flexWrap: fitToWidth ? "nowrap" : "wrap",
                overflow: fitToWidth ? "hidden" : "visible",
                minWidth: 0,
                width: fitToWidth ? "100%" : undefined,
                justifyContent: fitToWidth ? "flex-end" : undefined,
            }}
        >
            {shown.map((value) => {
                const followed = followAware && followedTags.includes(value);
                return (
                    <Chip
                        key={value}
                        label={value}
                        size="small"
                        variant={
                            followAware
                                ? followed
                                    ? "filled"
                                    : "outlined"
                                : undefined
                        }
                        onClick={
                            onTagClick
                                ? (e) => {
                                      // The whole card is clickable — don't
                                      // navigate when toggling a tag.
                                      e.stopPropagation();
                                      onTagClick(value);
                                  }
                                : undefined
                        }
                        sx={
                            followAware
                                ? {
                                      color: followed ? "#fff" : accentColor,
                                      bgcolor: followed
                                          ? accentColor
                                          : "transparent",
                                      borderColor: accentColor,
                                      fontWeight: 600,
                                      cursor: onTagClick
                                          ? "pointer"
                                          : undefined,
                                      "&:hover": {
                                          bgcolor: followed
                                              ? (accentDark ?? accentColor)
                                              : alpha(accentColor, 0.1),
                                      },
                                  }
                                : filled
                                  ? {
                                        // Color-matched outline, no tinted
                                        // fill: the old alpha(accent, .12)
                                        // wash + rose.dark text read as a
                                        // green tinge with red text.
                                        bgcolor: "transparent",
                                        border: `1px solid ${accentColor}`,
                                        color: accentColor,
                                        fontWeight: 600,
                                    }
                                  : {
                                        bgcolor: "transparent",
                                        border: `1px solid ${alpha(
                                            accentColor,
                                            0.5,
                                        )}`,
                                        color: accentColor,
                                    }
                        }
                    />
                );
            })}
            {extra > 0 && (
                <Chip
                    label={`+${extra}`}
                    size="small"
                    sx={{
                        bgcolor: "transparent",
                        border: `1px dashed ${alpha(accentColor, 0.4)}`,
                        color: "text.secondary",
                    }}
                />
            )}
        </Box>
    );
};

const LocationLine: React.FC<{ location: string }> = ({ location }) =>
    location ? (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "text.secondary",
                minWidth: 0,
            }}
        >
            <LocationOnIcon sx={{ fontSize: 16, flexShrink: 0 }} />
            <Typography variant="body2" noWrap>
                {location}
            </Typography>
        </Box>
    ) : null;

const ProfessionalCardBase: React.FC<ProfessionalCardProps> = ({
    professional,
    variant = "grid",
    accentColor = rose.primary,
    onClick,
    followedTags,
    onTagClick,
}) => {
    const isNavigable = Boolean(onClick);
    const location = formatLocation(professional.location);

    const avatar = (
        <Avatar
            src={professional.photoUrl || undefined}
            alt={professional.name}
            sx={{
                width: variant === "grid" ? 72 : 48,
                height: variant === "grid" ? 72 : 48,
                bgcolor: alpha(accentColor, 0.15),
                color: accentColor,
                fontWeight: 700,
                border: `2px solid ${alpha(accentColor, 0.35)}`,
            }}
        >
            {initialsOf(professional.name)}
        </Avatar>
    );

    const sharedCardSx = {
        position: "relative" as const,
        borderRadius: 1,
        boxShadow: 2,
        backgroundColor: "background.paper",
        border: "2px solid transparent",
        cursor: isNavigable ? "pointer" : "default",
        transition: "all 0.3s ease",
        "&:hover": isNavigable
            ? {
                  boxShadow: 10,
                  borderColor: alpha(accentColor, 0.5),
              }
            : undefined,
    };

    if (variant === "list") {
        return (
            <Card
                onClick={isNavigable ? onClick : undefined}
                data-select-target="Professional Row"
                sx={{ ...sharedCardSx, width: "100%" }}
            >
                <CardContent
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        py: 1.5,
                        px: 2,
                        "&:last-child": { pb: 1.5 },
                    }}
                >
                    {avatar}
                    <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 0,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                noWrap
                                sx={{ fontWeight: 600, color: accentColor }}
                            >
                                {professional.name}
                            </Typography>
                            {professional.isNew && (
                                <Chip
                                    label="NEW"
                                    size="small"
                                    color="success"
                                />
                            )}
                        </Box>
                        {professional.headline && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                            >
                                {professional.headline}
                            </Typography>
                        )}
                        <LocationLine location={location} />
                    </Box>
                    <Box sx={{ flexShrink: 0, display: "flex" }}>
                        <ContactButton
                            email={professional.email}
                            name={professional.name}
                            accentColor={accentColor}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: { xs: "none", sm: "flex" },
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 0.75,
                            flexShrink: 1,
                            minWidth: 0,
                            maxWidth: "45%",
                        }}
                    >
                        <ChipRow
                            values={professional.expertise || []}
                            accentColor={TAG_COLORS.discipline.primary}
                            accentDark={TAG_COLORS.discipline.dark}
                            filled
                            followedTags={followedTags}
                            onTagClick={onTagClick}
                            fitToWidth
                        />
                        <ChipRow
                            values={professional.languages || []}
                            accentColor={accentColor}
                            fitToWidth
                        />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card
            {...(isNavigable && onClick
                ? cardActivationProps(
                      `Open ${professional.name}'s profile`,
                      onClick,
                  )
                : {})}
            data-select-target="Professional Card"
            sx={{
                ...sharedCardSx,
                // Fills its CSS-grid cell on the index — the grid container
                // (auto-fill + minmax) owns the column sizing, so leftover
                // row space is divided evenly between the cards.
                width: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <CardContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                    flexGrow: 1,
                    p: 3,
                    "&:last-child": { pb: 3 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {avatar}
                    <Box sx={{ minWidth: 0 }}>
                        {professional.isNew && (
                            <Chip
                                label="NEW"
                                size="small"
                                color="success"
                                sx={{ mb: 0.5 }}
                            />
                        )}
                        <Typography
                            variant="h6"
                            component="h3"
                            sx={{
                                fontWeight: 600,
                                color: accentColor,
                                lineHeight: 1.25,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }}
                        >
                            {professional.name}
                        </Typography>
                        {professional.email && (
                            <Box sx={{ mt: 0.75 }}>
                                <ContactButton
                                    email={professional.email}
                                    name={professional.name}
                                    accentColor={accentColor}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>

                {professional.headline && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {professional.headline}
                    </Typography>
                )}

                <LocationLine location={location} />

                <Box
                    sx={{
                        mt: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                        pt: 0.5,
                    }}
                >
                    <ChipRow
                        values={professional.expertise || []}
                        accentColor={TAG_COLORS.discipline.primary}
                        accentDark={TAG_COLORS.discipline.dark}
                        filled
                        followedTags={followedTags}
                        onTagClick={onTagClick}
                    />
                    <ChipRow
                        values={professional.languages || []}
                        accentColor={accentColor}
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

export const ProfessionalCard = React.memo(ProfessionalCardBase);

export default ProfessionalCard;
